import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Search, Plus, Minus } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/points")({
  head: () => ({ meta: [{ title: "Puanlar — Admin" }] }),
  component: AdminPointsPage,
});

function AdminPointsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [percent, setPercent] = useState<string>("10");
  const [savingPercent, setSavingPercent] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    void (async () => {
      const { data: setting } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", "drink_points_percent")
        .maybeSingle();
      if (setting?.value !== undefined && setting?.value !== null) {
        setPercent(String(setting.value));
      }
      setPageLoading(false);
    })();
  }, [user, isAdmin]);

  if (loading || !user || !isAdmin || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-5xl px-4 py-10 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const savePercent = async () => {
    const num = Number(percent);
    if (Number.isNaN(num) || num < 0 || num > 100) {
      toast.error("Yüzde 0-100 arası olmalı.");
      return;
    }
    setSavingPercent(true);
    const { error } = await supabase
      .from("app_settings")
      .update({ value: num, updated_by: user.id })
      .eq("key", "drink_points_percent");
    setSavingPercent(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Puan oranı güncellendi.");
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />

        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-lg font-semibold">J kazanım oranı</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kullanıcı kart ile içecek satın aldığında, harcadığı tutarın bu yüzdesi kadar J kazanır.
            <br />
            <span className="text-xs">1 J = 1 ₺ değerinde. Kullanıcılar J bakiyelerini istedikleri tutarda kart ile yükler.</span>
          </p>
          <div className="mt-4 flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="percent">Yüzde (%)</Label>
              <Input
                id="percent"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
              />
            </div>
            <Button onClick={savePercent} disabled={savingPercent}>
              {savingPercent ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </div>
        </section>

        <ManualAdjustSection />
        <HistorySection />
      </div>
    </div>
  );
}

interface HistoryRow {
  id: string;
  amount: number;
  description: string | null;
  created_at: string;
  user_id: string;
  performed_by: string | null;
}

interface ProfileLite {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

function HistorySection() {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("point_transactions")
      .select("id, amount, description, created_at, user_id, performed_by")
      .eq("type", "admin_adjust")
      .not("performed_by", "is", null)
      .order("created_at", { ascending: false })
      .limit(100);
    const list = (data as HistoryRow[]) ?? [];
    setRows(list);
    const ids = Array.from(
      new Set(list.flatMap((r) => [r.user_id, r.performed_by].filter(Boolean) as string[])),
    );
    if (ids.length > 0) {
      const { data: pr } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", ids);
      const map: Record<string, ProfileLite> = {};
      for (const p of (pr as ProfileLite[]) ?? []) map[p.id] = p;
      setProfiles(map);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const fmt = (p?: ProfileLite) =>
    p ? `${p.first_name} ${p.last_name}`.trim() || p.email : "—";

  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">J yükleme geçmişi</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kim, hangi üyeye ne kadar J yüklediği veya düştüğü görünür. Son 100 işlem.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yenile"}
        </Button>
      </div>

      <div className="mt-4 space-y-2">
        {loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Henüz işlem yok.</p>
        )}
        {rows.map((r) => {
          const target = profiles[r.user_id];
          const admin = r.performed_by ? profiles[r.performed_by] : undefined;
          return (
            <div
              key={r.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{fmt(target)}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {fmt(admin)} tarafından · {new Date(r.created_at).toLocaleString("tr-TR")}
                </div>
                {r.description && (
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">{r.description}</div>
                )}
              </div>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  r.amount >= 0 ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
                }`}
              >
                {r.amount >= 0 ? "+" : ""}
                {r.amount} J
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface MemberRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  points_balance: number;
}

function ManualAdjustSection() {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<MemberRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<MemberRow | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const runSearch = async () => {
    setSearching(true);
    const q = search.trim();
    let query = supabase
      .from("profiles")
      .select("id, first_name, last_name, email, points_balance")
      .eq("membership_status", "approved")
      .order("first_name", { ascending: true })
      .limit(20);
    if (q) {
      query = query.or(
        `first_name.ilike.%${q}%,last_name.ilike.%${q}%,email.ilike.%${q}%`,
      );
    }
    const { data, error } = await query;
    setSearching(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setResults((data as MemberRow[]) ?? []);
  };

  useEffect(() => {
    void runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adjust = async (sign: 1 | -1) => {
    if (!selected) return;
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) {
      toast.error("Geçerli bir miktar gir.");
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.rpc("admin_adjust_points", {
      _user_id: selected.id,
      _amount: Math.trunc(num) * sign,
      _description: note || undefined,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const newBalance = (data as { new_balance?: number } | null)?.new_balance ?? selected.points_balance;
    toast.success(sign > 0 ? `${num} J yüklendi` : `${num} J düşüldü`);
    setSelected({ ...selected, points_balance: newBalance });
    setResults((prev) => prev.map((r) => (r.id === selected.id ? { ...r, points_balance: newBalance } : r)));
    setAmount("");
    setNote("");
  };

  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="text-lg font-semibold">Üyeye J yükle</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Üye seç, miktarı yaz ve yükle veya düş. İşlem J geçmişine kaydedilir.
      </p>

      <div className="mt-4 flex gap-2">
        <Input
          placeholder="Ad, soyad veya e-posta ile ara"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void runSearch();
          }}
        />
        <Button variant="outline" onClick={() => void runSearch()} disabled={searching}>
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-2">
        {results.map((m) => {
          const active = selected?.id === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              className={`rounded-xl border p-3 text-left transition-colors ${
                active
                  ? "border-primary bg-primary/5"
                  : "border-border bg-background hover:bg-muted"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {m.first_name} {m.last_name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                </div>
                <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold">
                  {m.points_balance} J
                </span>
              </div>
            </button>
          );
        })}
        {results.length === 0 && !searching && (
          <p className="text-sm text-muted-foreground md:col-span-2">Sonuç bulunamadı.</p>
        )}
      </div>

      {selected && (
        <div className="mt-6 rounded-xl border border-border bg-background p-4">
          <p className="text-sm">
            Seçili: <span className="font-medium">{selected.first_name} {selected.last_name}</span>{" "}
            <span className="text-muted-foreground">— mevcut bakiye {selected.points_balance} J</span>
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[160px_1fr]">
            <div>
              <Label htmlFor="amount">Miktar (J)</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="note">Açıklama (opsiyonel)</Label>
              <Input
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Örn. doğum günü hediyesi"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => void adjust(1)} disabled={submitting}>
              <Plus className="mr-1.5 h-4 w-4" /> J yükle
            </Button>
            <Button variant="outline" onClick={() => void adjust(-1)} disabled={submitting}>
              <Minus className="mr-1.5 h-4 w-4" /> J düş
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
